/**
 * The single money formatter for the app.
 *
 * Every payload ships `currency_symbol` next to the amount, resolved from
 * CurrencyEnum::symbol() on the backend — so there is no symbol table here to drift
 * out of step with it.
 *
 * Amounts are never converted or compared across currencies, so there is no default
 * currency to fall back on: a value with no symbol renders bare rather than being
 * labelled with one it may not belong to.
 */
export const formatMoneyWithSymbol = (
    amount: number | null | undefined,
    symbol: string | null | undefined,
    { fallback = '—' }: { fallback?: string } = {},
): string => {
    if (amount === null || amount === undefined || Number.isNaN(amount)) {
        return fallback
    }

    return symbol ? `${symbol} ${amount.toFixed(2)}` : amount.toFixed(2)
}

/**
 * A price movement, as a signed percentage.
 *
 * A percentage is the one price figure that survives having no currency attached —
 * it is a ratio within a single series, not an amount — so this needs no symbol.
 */
export const formatPriceChange = (value: number | null | undefined): string | null => {
    if (value === null || value === undefined) {
        return null
    }

    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`
}

/**
 * The colour a price movement reads in: up is good, down is bad, flat and unknown
 * are neither.
 *
 * Beside `formatPriceChange` because the two are always used together, and because
 * the rule was written out three times across the cards that show a change.
 */
export const priceChangeClass = (value: number | null | undefined) => ({
    'text-emerald-600': value !== null && value !== undefined && value > 0,
    'text-red-600': value !== null && value !== undefined && value < 0,
    'text-muted-foreground': value === null || value === undefined || value === 0,
})
